import type {ContentLayerProps} from '@travel-frontend/ui-components';

import {useAsync} from '@react-hookz/web';
import {
  ContentLayer,
  ContentLayerBody,
  Skeleton,
} from '@travel-frontend/ui-components';
import axios from 'axios';
import {useEffect} from 'react';
import {useTranslation} from 'react-i18next';

import {TooltipMarkdown} from '@/components/TooltipMarkdown';

type Conditions = {
  title: string;
  description: string;
  content: string;
};

type Props = Pick<ContentLayerProps, 'open' | 'onClose'> & {
  identifier: string;
};

type Response = {
  data: {
    conditions: Array<{
      title: string;
      description: string;
      content: string;
    }>;
  };
};

const fetchConditions = async ({
  identifier,
  abortSignal,
}: {
  identifier: string;
  abortSignal?: AbortSignal;
}): Promise<Conditions | null> => {
  const response = await axios<Response>({
    method: 'post',
    url: import.meta.env.VITE_SERVICE_DIRECTUS_URL,
    data: {
      query: `query Conditions {
        conditions(
          filter: {
            status: {
              _eq: "published"
            },
            platforms: {
              platforms_id: {
                agent: {
                  _eq: "${import.meta.env.VITE_PORTAL}"
                }
              }
            },
            identifier: {
              _eq: "${identifier}"
            },
          },
          limit: 1
        ) {
          title
          description
          content
        }
      }`,
    },
    signal: abortSignal,
  });

  if (
    response.status !== 200 ||
    response.data.data.conditions[0] === undefined
  ) {
    return null;
  }

  return response.data.data.conditions[0];
};

export const ConditionsLayer = ({identifier, open, onClose}: Props) => {
  const {t} = useTranslation(['conditionsOverlay']);

  const [{result, status}, actions] = useAsync<
    Conditions | null,
    [AbortSignal]
  >(
    (abortSignal) =>
      fetchConditions({
        identifier,
        abortSignal,
      }),
    null
  );

  useEffect(() => {
    const abortController = new AbortController();
    if (open && identifier) {
      actions.execute(abortController.signal);
    }
    return () => abortController.abort();
  }, [actions, open, identifier]);

  if (!identifier) {
    return null;
  }

  const isLoading = status === 'loading';

  const title = isLoading ? (
    <Skeleton variant="text" width={250} height={32} />
  ) : (
    (result?.title ?? '')
  );

  const content = isLoading ? (
    <>
      <Skeleton variant="text" width={250} height={46} />
      <Skeleton variant="text" width={110} height={32} />
      <Skeleton variant="text" width={230} height={32} />
      <Skeleton variant="text" width={200} height={32} />
    </>
  ) : (
    <div>
      <TooltipMarkdown value={result?.content ?? ''} />
    </div>
  );

  return (
    <ContentLayer
      open={open}
      closeButtonLabel={t('closeButtonLabel')}
      title={title}
      description={result?.description}
      onClose={onClose}
    >
      <ContentLayerBody>{content}</ContentLayerBody>
    </ContentLayer>
  );
};
